import React, { useEffect, useState } from "react";
import BarcodeReactScanner from "barcode-react-scanner";
import { Box, Button, Flex, Text } from "ui-components";
import get from "lodash/get";
import { isFunction } from "lodash";
import { gotoUrl, urlParam } from "utils/helpers";
import FormattedMessage from "components/FormattedMessage";
import { buttonThemes, getButtonColorVariant } from "ui-components/theme";
import StaticContentBox from "components/StaticContentBox/index";

const BarcodeScanner = props => {
  const {
    change,
    scanned_field = "icc_id",
    scanning_field = "scanning_barcode",
    scan_button_label = "scan_barcode_with_camera",
    // data,
    formValues,
    iccIdPrefix,
    showButton = false,
    themeColors
  } = props;

  const redirectUrl = urlParam("redirect");
  // const redirectUrl = get(data, "redirectUrl");
  const scanningBarcode = get(formValues, scanning_field);
  const buttonTheme = get(themeColors, "button") || buttonThemes.blue;
  const [invalidIccid, setInvalidIccid] = useState(false); // true, false
  const [hasCamera, setHasCamera] = useState(null); // true, false, or null (initial loading)
  const [permission, setPermission] = useState("unknown"); // "granted", "denied", "unknown"

  const checkCameraPresence = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(
        device => device.kind === "videoinput"
      );
      setHasCamera(hasVideoInput);
      return hasVideoInput;
    } catch (err) {
      console.error("Error checking for camera:", err);
      setHasCamera(false);
      return false;
    }
  };

  const requestPermission = async () => {
    const hasCam = permission !== "denied" ? await checkCameraPresence() : true;
    if (!hasCam) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setPermission("granted");
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      setPermission("denied");
    }
  };

  const isValidIccid = code => {
    const hasPrefix = Array.isArray(iccIdPrefix)
      ? iccIdPrefix.some(prefix => code.startsWith(prefix))
      : code.startsWith(iccIdPrefix);

    const strippedCode = hasPrefix
      ? code.replace(new RegExp(`^${iccIdPrefix}`), "")
      : code;

    const expectedLength = hasPrefix ? 10 : 18;
    const validRegex = new RegExp(`^\\d{${expectedLength}}$`);

    return validRegex.test(strippedCode);
  };

  useEffect(() => {
    requestPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderBarcode = () => {
    return (
      <div>
        <BarcodeReactScanner
          onUpdate={(err, resp) => {
            if (resp) {
              console.log("scanned", resp);
              const code = resp.getText();

              if (code) {
                let finalCode = code;

                if (iccIdPrefix) {
                  const prefixes = Array.isArray(iccIdPrefix)
                    ? iccIdPrefix
                    : [iccIdPrefix];
                  const matchedPrefix = prefixes.find(prefix =>
                    finalCode.startsWith(prefix)
                  );
                  if (matchedPrefix) {
                    finalCode = finalCode.slice(matchedPrefix.length);
                  }
                }

                const valid = isValidIccid(code);
                if (!valid) {
                  setInvalidIccid(true);
                  return;
                }

                if (isFunction(change)) {
                  change(scanned_field, finalCode);

                  if (redirectUrl) {
                    redirectUrl && gotoUrl(redirectUrl);
                  } else {
                    change(scanning_field, false);
                  }
                }
              }
            }
            if (err) {
              console.log(err);
            }
          }}
        />
      </div>
    );
  };

  const CornerOverlay = () => {
    const cornerStyle = {
      position: "absolute",
      width: "40px",
      height: "4px",
      backgroundColor: "white",
      borderRadius: "2px",
      margin: 10
    };

    const verticalStyle = {
      ...cornerStyle,
      width: "4px",
      height: "40px"
    };

    const centerLineStyle = {
      position: "absolute",
      backgroundColor: "white",
      borderRadius: "2px",
      margin: 10
    };

    return (
      <>
        {/* Corners */}
        <Box sx={{ ...cornerStyle, top: 0, left: 0 }} />
        <Box sx={{ ...cornerStyle, top: 0, right: 0 }} />
        <Box sx={{ ...cornerStyle, bottom: 0, left: 0 }} />
        <Box sx={{ ...cornerStyle, bottom: 0, right: 0 }} />
        <Box sx={{ ...verticalStyle, top: 0, left: 0 }} />
        <Box sx={{ ...verticalStyle, bottom: 0, left: 0 }} />
        <Box sx={{ ...verticalStyle, top: 0, right: 0 }} />
        <Box sx={{ ...verticalStyle, bottom: 0, right: 0 }} />

        {/* Center Lines */}
        <Box
          sx={{
            ...centerLineStyle,
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60px",
            height: "4px"
          }}
        />
        <Box
          sx={{
            ...centerLineStyle,
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60px",
            height: "4px"
          }}
        />
        <Box
          sx={{
            ...centerLineStyle,
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: "4px",
            height: "60px"
          }}
        />
        <Box
          sx={{
            ...centerLineStyle,
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: "4px",
            height: "60px"
          }}
        />
      </>
    );
  };

  return !scanningBarcode && showButton ? (
    <Button
      mx={1}
      width={[1]}
      sx={getButtonColorVariant(buttonTheme)}
      onClick={() => {
        change(scanning_field, true);
      }}
    >
      <FormattedMessage html id={scan_button_label} />
    </Button>
  ) : (
    <>
      <Flex
        sx={{
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "fit-content",
          margin: "auto"
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: "1",
            width: "90%",
            aspectRatio: permission === "denied" ? "1" : "0",
            backgroundColor: "#000",
            borderRadius: "16px",
            overflow: "hidden"
          }}
        >
          {permission === "granted" && renderBarcode()}
          <CornerOverlay />
        </Box>
        <div className="p-4 text-center space-y-4">
          {hasCamera === null && <Text>Checking for camera...</Text>}

          {hasCamera === false && (
            <StaticContentBox
              content={"No camera detected on this device."}
              alert={true}
              themeColors={themeColors}
            />
          )}

          {hasCamera && permission === "unknown" && (
            <Text>Checking camera permission...</Text>
          )}

          {hasCamera && permission === "denied" && (
            <>
              <StaticContentBox
                content={
                  "sim_verification_scanner_permission_required_info_msg"
                }
                alert={true}
                themeColors={themeColors}
              />
              <Button
                mx={1}
                width={[1]}
                sx={getButtonColorVariant(buttonTheme)}
                onClick={() => requestPermission()}
              >
                <FormattedMessage
                  id={"sim_verification_scanner_retry_permission"}
                />
              </Button>
            </>
          )}
        </div>
        {invalidIccid && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <StaticContentBox
              content={"sim_verification_scanner_invalid_iccid_msg"}
              alert={true}
              themeColors={themeColors}
              style={{
                backgroundColor: "transparent",
                color: "red",
                marginBottom: 0
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Text fontWeight="normal" color="#475467">
            <FormattedMessage id={"sim_verification_scanner_interruptions"} />
          </Text>
          <Button
            width={[1]}
            padding={0}
            sx={getButtonColorVariant(buttonTheme, "link")}
            onClick={() => {
              change(scanning_field, false);
              gotoUrl(`${redirectUrl}?manual_mode=true`);
            }}
          >
            <FormattedMessage id={"enter_code_manually"} />
          </Button>
        </Box>
      </Flex>
    </>
  );
};

export default BarcodeScanner;
